import React, { RefObject, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { AnimationClip, AnimationMixer, WebGLRenderer, Scene, PerspectiveCamera, Clock, AnimationAction } from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import { AnimationData } from "../../assets/glb/animations";
import { Container } from "./styles";

interface LisaAvatarProps {
  animatingSpeed: number;
  animation: AnimationData[];
  repeat: boolean;
  setRepeat: React.Dispatch<React.SetStateAction<boolean>>;
  setAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  isHomeActive?: boolean;
  isPaused: boolean;
  parentDiv: RefObject<HTMLDivElement>;
  setPlayerTimeMs: (time: number) => void;
  setTotalDuration: (duration: number) => void;
}

export function LisaAvatar({
  animatingSpeed,
  animation,
  repeat,
  setRepeat,
  setAnimating,
  isHomeActive = false,
  isPaused,
  parentDiv,
  setPlayerTimeMs,
  setTotalDuration
}: LisaAvatarProps) {
  
  const [renderer] = useState(() => new WebGLRenderer({ antialias: true, alpha: true }));
  const [scene] = useState(() => new Scene());
  const [camera] = useState(() => new PerspectiveCamera());
  const [clock] = useState(() => new Clock());
  const [mixer, setMixer] = useState<AnimationMixer>();
  const [clips, setClips] = useState<AnimationClip[]>();
  const [actions, setActions] = useState<AnimationAction[]>();
  const [idleClip, setIdleClip] = useState<AnimationClip>();
  const animationTimeToEnd = useRef(0);
  const totalCalculatedDurationRef = useRef(0);
  const fadeDuration = 0.2;
  const isPausedRef = useRef(false);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);
  
  const animationSpeedRef = useRef(animatingSpeed); 

  const trackClassMap: { [key: string]: typeof THREE.KeyframeTrack } = {
    'vector': THREE.VectorKeyframeTrack,
    'quaternion': THREE.QuaternionKeyframeTrack,
    'number': THREE.NumberKeyframeTrack, 
    'color': THREE.ColorKeyframeTrack,
    'scalar': THREE.NumberKeyframeTrack,
  };

  function setScene(){
    renderer.outputEncoding = THREE.sRGBEncoding;
    setCamera();
    setLighting();
  }

  function setCamera() {
    camera.position.set(0, 4.7, 10);
    camera.rotation.set(-0.05, 0, 0);
    camera.scale.set(1, 1, 4.5);
    camera.fov = 85;
  }

  function setLighting() {
    const backLight = new THREE.DirectionalLight(0xffebeb, 1.3);
    backLight.position.set(-2.3, 6.3, 4.3);
    scene.add(backLight);

    const backLight2 = new THREE.DirectionalLight(0xffebeb, 0.5);
    backLight2.position.set(-1.0, -6.3, 1.3);
    scene.add(backLight2);

    const rimLight = new THREE.DirectionalLight(0xeaebff, 1.2);
    rimLight.position.set(0.6, 3.5, 6.2);
    scene.add(rimLight);

    const keyLight = new THREE.DirectionalLight(0xeaebff, 1);
    keyLight.position.set(3.1, 0.7, 3.2);
    scene.add(keyLight);
  }

  function addLisaInDOM(lisaFile: string) {
    loadModel(lisaFile);
    const lisaAvatarElement = document.getElementById("lisaAvatar");
    if (lisaAvatarElement && lisaAvatarElement.firstChild) {
      lisaAvatarElement.removeChild(lisaAvatarElement.firstChild);
    }
    if (lisaAvatarElement) lisaAvatarElement.appendChild(renderer.domElement);
  }

  function loadModel(lisaFile: string) {
    const loader = new GLTFLoader();

    loader.load(lisaFile, (gltf: { scene: any; animations: any; }) => {
      const gltfScene = gltf.scene;
      scene.add(gltfScene);
      
      const mixer = new AnimationMixer(gltfScene);
      const idleClip = AnimationClip.findByName(gltf.animations, "IDLE");

      if (idleClip){
        setIdleClip(idleClip);
        if (mixer) {
          const idleAction = mixer.clipAction(idleClip);
          idleAction.play();
        }
      }
      setMixer(mixer);
    });
  }

  function setIdle(mixer: AnimationMixer, startAt: number) {
    if (idleClip) {
      const idleAction = mixer.clipAction(idleClip);

      if (idleAction) {
        idleAction.startAt(startAt);
        idleAction.play();
      }
    }
  }

  function stopCurrentAnimation() {
    if (!mixer || !idleClip) return;

    mixer.stopAllAction();

    const idleAction = mixer.clipAction(idleClip);
    idleAction.reset();
    idleAction.play();

    animationTimeToEnd.current = 0;
    totalCalculatedDurationRef.current = 0;

    setPlayerTimeMs(0);
    setTotalDuration(0);
  }


  function adjustScreenSize(width: number, height: number) {
    renderer.setSize(width, height);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  }

  function startLoop() {
    renderer.setAnimationLoop(() => {
      if (!mixer) return;

      const delta = clock.getDelta();

      if (animationSpeedRef.current > 0) {
        if (!isPausedRef.current) {
          mixer.update(delta * animationSpeedRef.current);
        } else {
          mixer.update(0);
        }
      }

      if (totalCalculatedDurationRef.current > 0) {
        const startTime =
          animationTimeToEnd.current - totalCalculatedDurationRef.current;

        const currentTime = Math.max(0, mixer.time - startTime);
        setPlayerTimeMs(currentTime * 1000);
      }

      if (
        animationTimeToEnd.current &&
        mixer.time >= animationTimeToEnd.current
      ) {
        setAnimating(false);
      }

      renderer.render(scene, camera);
    });
  }


  function customArraySearch(array: number[] | Float32Array, val: number): number {
    for (let i = 0; i < array.length; i++) {
      if (array[i] >= val) {
        return Math.max(0, i - 1);
      }
    }
    return array.length - 1;
  }

  function getTrackValueAtTime(track: THREE.KeyframeTrack, time: number, target: Float32Array): void {
    if (!track || !track.times || track.times.length === 0 || !track.values || track.values.length === 0) {
        target.fill(0);
        return;
    }
    
    const times = track.times ? track.times : [];
    const values = track.values;
    const valueSize = track.getValueSize();
    
    const startIdx = customArraySearch(times, time);

    if (startIdx <= 0) {
      const valueOffset = 0;
      for (let i = 0; i < valueSize; i++) {
        target[i] = values[valueOffset + i];
      }
      return;
    }

    if (times[startIdx] === time) {
      const valueOffset = startIdx * valueSize;
      for (let i = 0; i < valueSize; i++) {
        target[i] = values[valueOffset + i];
      }
      return;
    }

    if (startIdx >= times.length - 1) {
      const lastIdx = times.length - 1;
      const valueOffset = lastIdx * valueSize;
      for (let i = 0; i < valueSize; i++) {
        target[i] = values[valueOffset + i];
      }
      return;
    }
    
    const time0 = times[startIdx];
    const time1 = times[startIdx + 1];
    const t = (time - time0) / (time1 - time0); 

    const offset0 = startIdx * valueSize;
    const offset1 = (startIdx + 1) * valueSize;
    
    for (let i = 0; i < valueSize; i++) {
      const val0 = values[offset0 + i];
      const val1 = values[offset1 + i];
      target[i] = val0 + (val1 - val0) * t;
    }
  }

  function createCrossFadeClip(
    clipA: THREE.AnimationClip, 
    clipB: THREE.AnimationClip, 
    fadeDuration: number
  ): THREE.AnimationClip | null {
    
    const timeA = clipA.duration;
    const timeB = 0;

    const newTracks: THREE.KeyframeTrack[] = [];
    
    for (const trackA of clipA.tracks) {
      const trackB = clipB.tracks.find(t => t.name === trackA.name)!;
      const trackType = trackA.ValueTypeName.toLowerCase();
      const KeyframeTrackClass = trackClassMap[trackType]; 
      
      const numComponents = trackA.getValueSize();
      const valueA = new Float32Array(numComponents); 
      const valueB = new Float32Array(numComponents); 
      const values = new Float32Array(numComponents * 2); 

      getTrackValueAtTime(trackA, timeA, valueA); 
      getTrackValueAtTime(trackB, timeB, valueB);

      values.set(valueA, 0); 
      values.set(valueB, numComponents); 

      const times = [0, fadeDuration];

      const newTrack = new KeyframeTrackClass(
        trackA.name, 
        times,
        values
      );

      newTracks.push(newTrack);
    }

    const crossFadeClip = new THREE.AnimationClip(`Interpolation_from_${clipA.name}_to_${clipB.name}`, fadeDuration, newTracks);
    return crossFadeClip;
  }

  function getClipFromData(animationData: AnimationData): THREE.AnimationClip {
    const animation = {
      ...animationData.animation,
      tracks: animationData.animation.tracks 
    }
    return THREE.AnimationClip.parse(animation as any) 
  }

  function getDataFromClip(animationClip: THREE.AnimationClip): AnimationData {
    const clipJSON = AnimationClip.toJSON(animationClip); 
    
    let data: AnimationData = {
      animation: {
        name: clipJSON.name,
        duration: clipJSON.duration,
        tracks: clipJSON.tracks, 
        uuid: clipJSON.uuid,
        blendMode: clipJSON.blendMode
      },
      glosa: animationClip.name,
    };
    return data;
  }

  function loadSceneActions(animations: AnimationData[]) {
    if (!mixer) return;

    let animationStartValue = mixer.time;
    const actions: THREE.AnimationAction[] = [];
    const clips: AnimationClip[] = [];

    const animationsParsed: any[] = [];

    animations.forEach((a, index) => {
      let animation = a.animation;

      const currentAnimation = {
        ...animation,
        tracks: animation.tracks
      }

      let transition: AnimationClip;
      let lastClip: AnimationClip;
      const currentClip = getClipFromData(a);

      if (index === 0) {
        lastClip = idleClip!;
        transition = createCrossFadeClip(lastClip, currentClip, fadeDuration)!;
      } else {
        lastClip = getClipFromData(animations[index-1]);
      }
      transition = createCrossFadeClip(lastClip, currentClip, fadeDuration)!;
      animationsParsed.push(getDataFromClip(transition).animation);
      animationsParsed.push(currentAnimation);
    });

    const finalTransition = createCrossFadeClip(getClipFromData(animations[animations.length-1]), idleClip!, fadeDuration)!;
    animationsParsed.push(getDataFromClip(finalTransition).animation);

    mixer.stopAllAction();

    animationsParsed.forEach((jsonTrack, index) => {
      const clip = AnimationClip.parse(jsonTrack);
      let action = mixer.existingAction(clip);
      if (action) {
        if (actions.find((a) => a === action)) {
          clip.uuid = clip.uuid.concat(String(index));
          action = mixer.clipAction(clip);
        } else {
          action.reset();
        }
      } else {
        action = mixer.clipAction(clip);
      }

      action.setLoop(THREE.LoopRepeat, 1);

      action.startAt(animationStartValue);
      action.play();
      mixer.update(0.0001);

      actions.push(action);
      clips.push(clip);
      animationStartValue += clip.duration;
    });

    const totalDurationReal = animationStartValue - mixer.time;
    totalCalculatedDurationRef.current = totalDurationReal;
    animationTimeToEnd.current = animationStartValue;
    setTotalDuration(totalDurationReal);
    setIdle(mixer, animationStartValue);

    setClips(clips);
    setActions(actions);
  }

  function repeatCurrentAnimation() {
    if (mixer && actions && clips) {
      let animationStartValue = mixer.time;
      const initialMixerTime = mixer.time;
      mixer.stopAllAction();
      actions.forEach((action, index) => {
        action.stop(); 
        action.startAt(animationStartValue);
        action.play();

        animationStartValue += clips[index].duration;
      });

      totalCalculatedDurationRef.current = animationStartValue - initialMixerTime;
      animationTimeToEnd.current = animationStartValue;
      setIdle(mixer, animationStartValue);
      setRepeat(false);
    }
  }

  useEffect(() => {
    animationSpeedRef.current = animatingSpeed;
  }, [animatingSpeed]);
  
  useEffect(() => {
    const lisaFile = './lisa.glb';
    addLisaInDOM(lisaFile);
    setScene();
    
    return () => {
      renderer.setAnimationLoop(null); 
      const lisaAvatarElement = document.getElementById("lisaAvatar");
      if (lisaAvatarElement && renderer.domElement) {
        lisaAvatarElement.removeChild(renderer.domElement);
      }
      renderer.dispose();
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          if (object.geometry) object.geometry.dispose();
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else if (object.material) {
            object.material.dispose();
          }
        }
      });
    };
  }, []);

  useEffect(() => {
    const element = parentDiv.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        for (let entry of entries) {
          const { width, height } = entry.contentRect;
          adjustScreenSize(width, height);
        }
      });
    });

    observer.observe(element);
    return () => { observer.disconnect(); };
  }, [parentDiv]);

  useEffect(() => {
    startLoop();
    return () => {
      renderer.setAnimationLoop(null);
    }
  }, [mixer, renderer]);

  useEffect(() => {
    const onInterrupt = () => stopCurrentAnimation();

    window.addEventListener("LISA_INTERRUPT", onInterrupt);

    return () => {
      window.removeEventListener("LISA_INTERRUPT", onInterrupt);
    };
}, [mixer, idleClip]);


  useEffect(() => {
    if (!mixer) return;

    if (animation.length > 0) {
      loadSceneActions(animation);
      window.dispatchEvent(new CustomEvent("LISA_ANIMATION_STARTED"));
    }
}, [animation, mixer]);


  useEffect(() => {
    if (repeat) repeatCurrentAnimation();
  }, [repeat, mixer, actions, clips]);

  return (
    <Container
      id="lisaAvatar"
      isHomeActive={isHomeActive}
    />
  );
}